import { useState } from "react";
import { TrashIcon, FlagIcon, FlagFilledIcon } from "../assets/icons/index.js";
import { useGlobalContext } from "../context/GlobalContext.jsx";
import requestHandler from "../utils/requestHandler.js";

function Comment({ comment }) {
  const { user: currentUser } = useGlobalContext();
  const { user, content, created_at, _id, id, user_flagged = false } = comment;
  const commentId = _id || id;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isFlagged, setIsFlagged] = useState(user_flagged);

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    const res = await requestHandler({
      path: `comments/${commentId}`,
      method: "DELETE",
    });

    if (!res.success) {
      console.error("Error deleting comment:", res.error);
    } else {
      window.location.reload();
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleFlag = async () => {
    setIsFlagged(!isFlagged);

    const res = await requestHandler({
      path: "flags/toggle",
      method: "POST",
      body: { comment_id: commentId },
    });

    if (!res.success) {
      setIsFlagged(!isFlagged);
      console.error("Error toggling comment flag:", res.error);
    }
  };

  const DeleteConfirmationModal = ({ isOpen, onConfirm, onCancel }) => {
    if (!isOpen) return null;

    return (
      <>
        {/* Overlay for the entire app */}
        <div className="fixed inset-0 bg-black/50 z-50" onClick={onCancel} />

        {/* Modal content */}
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={onCancel}
        >
          <div
            className="bg-white dark:bg-lz-ink-mid text-lz-ink-mid dark:text-white rounded-2xl p-6 shadow-lg w-96"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-semibold mb-4">Delete Comment</h3>
            <p className="mb-6">
              Are you sure you want to delete this comment? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-lz-ink-mid dark:text-white hover:bg-gray-100 dark:hover:bg-lz-charcoal rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      <div className="flex flex-col gap-2 py-4 border-b border-lz-paper-border dark:border-lz-charcoal-border last:border-b-0 relative">
        {/* Action buttons */}
        <div className="absolute top-4 right-0 flex gap-1">
          {/* Flag Button */}
          <button
            onClick={handleFlag}
            className="p-2 hover:bg-white dark:hover:bg-lz-charcoal transition-colors"
            aria-label={isFlagged ? "Unflag comment" : "Flag comment"}
          >
            {isFlagged ? (
              <FlagFilledIcon className="w-4 h-4 fill-lz-ember" />
            ) : (
              <FlagIcon className="w-4 h-4 dark:fill-lz-concrete fill-lz-charcoal" />
            )}
          </button>

          {/* Trash Icon - only show if current user owns the comment */}
          {currentUser?.id && user?.id && currentUser.id === user.id && (
            <button
              onClick={handleDeleteClick}
              className="p-2 hover:bg-white dark:hover:bg-lz-charcoal transition-colors"
              aria-label="Delete comment"
            >
              <TrashIcon className="w-4 h-4 fill-lz-charcoal dark:fill-lz-concrete hover:fill-red-500 dark:hover:fill-red-400 transition-colors" />
            </button>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-semibold text-lz-gold-border">
            {user?.name || user || "Anonymous"}
          </span>
          <span className="font-semibold text-lz-ink-mid/70 dark:text-lz-concrete/70">
            {created_at ? new Date(created_at).toLocaleDateString("en-US") : ""}
          </span>
        </div>
        <p className="text-black dark:text-lz-concrete leading-relaxed">
          {content.split("\n").map((item) => {
            return (
              <>
                {item}
                <br />
              </>
            );
          })}
        </p>
      </div>
    </>
  );
}

export default function CommentThread({ comments }) {
  return (
    <div className="pl-8">
      <div className="space-y-0">
        {comments.map((comment, index) => (
          <Comment key={comment._id || comment.id || index} comment={comment} />
        ))}
      </div>
    </div>
  );
}
